import querystring from 'query-string'
import omit from 'lodash-es/omit'
import sitesConfig from '../../config/sites'
import {
  validSortByOptions,
  searchFrontendParamsSeoTransform,
  searchFrontendLowerCaseParams
} from '../constants/searchQueryWhitelist'
import filterParamsConfig from '../constants/filterParamsConfig'
import { sanitizeQueryParamString } from './sanitize'
import { hasSearchPageQueryParams } from './location'

export { sanitizeFullUrl } from './sanitize'

/**
 * check the top level pathname
 * /category/wordpress -> category
 * /search?category=wordpress -> search
 * /tags/clean -> tags
 * @param  {String} pathname
 * @return {String} top level route name
 */
export const getTopLevelRoute = (pathname = '/') => {
  return pathname.split('/').slice(1, 2).join('')
}

export const queryWithoutFilters = locationParams => {
  const filterParamsKeys = Object.keys(filterParamsConfig)

  return getQueryWithoutParam(locationParams, filterParamsKeys)
}

export const activeFiltersParams = locationParams => {
  const filterParamsKeys = Object.keys(filterParamsConfig)
  const paramsList = Object.keys(locationParams)

  return filterParamsKeys.filter(param => paramsList.includes(param))
}

const slugifyQueries = queries => {
  return Object.keys(queries).reduce((result, key) => {
    if (queries[key]) {
      let slugifiedValue = queries[key].toString()
      if (searchFrontendLowerCaseParams.includes(key)) {
        slugifiedValue = slugifiedValue.toLowerCase()
      }
      if (searchFrontendParamsSeoTransform.includes(key)) {
        slugifiedValue = slugifiedValue.replace(/_/g, '-')
      }
      let decodedValue = null
      try {
        decodedValue = decodeURIComponent(slugifiedValue)
      } catch (e) {
        decodedValue = slugifiedValue
      }
      result[key] = decodedValue
    }
    return result
  }, {})
}

/**
 * a function to parse current location to a query object
 * it should handle all possible url
 * /search?category=wordpress
 * /search?category=wordpres,site-template
 * /search?tags=clean&category=wordpress&sales=rank-3
 * /category/wordpress
 * /category/wordpress/ecomerce
 * /tags/clean
 * /tags/clean,modern
 * /attributes/themeforest-files-included/css-files
 * /attributes/compatible-software/Wordpress 4.5.2
 * @param {Object} The location object from react-router
 * {
 *   pathname: '/',
 *   query: {
 *     term: 'cat'
 *   }
 * }
 * @param {Boolean} Whether to strip 'all' or '' category from parsed location
 * @return {Object} a object of all the seach queries
 */
export const parseLocation = (
  { pathname = '/search', query = {} } = {},
  stripAllCategory = true
) => {
  const categoryPageRegex = /\/(category)\/+/g
  const tagsPageRegex = /\/(tags)\/+/g
  const attributesPageRegex = /\/(attributes)\/+/g
  const searchPageRegex = /\/(search)\/*/g

  const topLevelRoute = getTopLevelRoute(pathname)

  // the last win if there are same queries in the url
  const sortedQuery = Object.keys(query)
    .filter(param => query[param] !== null && query[param] !== '')
    .reduce((prev, curr) => {
      if (Array.isArray(query[curr])) {
        // colors=red&colors=yellow -> colors=red,yellow
        prev[curr] = query[curr].join(',')
      } else {
        prev[curr] = query[curr]
      }
      return prev
    }, {})

  let queries
  switch (topLevelRoute) {
    case 'category': {
      let categoryName = pathname.replace(categoryPageRegex, '')
      queries = Object.assign({}, sortedQuery, {
        category: categoryName
      })
      // when the category is equal to `all` from the url
      // it's passing empty string to search sortedQuery
      if ((categoryName === '' || categoryName === 'all') && stripAllCategory) {
        delete queries.category
      }
      break
    }

    case 'tags':
      queries = Object.assign({}, sortedQuery, {
        tags: pathname.replace(tagsPageRegex, '')
      })
      break
    case 'attributes': {
      let attributes = pathname.replace(attributesPageRegex, '')
      queries = Object.assign({}, sortedQuery, {
        attribute_key: attributes.split('/')[0],
        attribute_value: attributes.split('/')[1] || ''
      })
      break
    }

    case 'search': {
      let newTerm = sortedQuery.term || pathname.replace(searchPageRegex, '')
      queries = Object.assign({}, sortedQuery, { term: newTerm })
      break
    }

    default:
      queries = Object.assign({}, sortedQuery)
  }

  let slugifiedQueries = slugifyQueries(queries)

  const sortOption = slugifiedQueries.sort

  if (sortOption) {
    // in the url we only store the sort filter as `search?sort=trending'
    // but in market-search api it matches to `sort_by` and `sort_direction`
    // so whenever we match `sort` from the url, we have to parse it to
    // `sort_by` and `sort_direction` so we could fetch the correct result
    // this is also used in rendering the selected filters
    if (sortOption.indexOf('price-') >= 0) {
      slugifiedQueries = Object.assign({}, slugifiedQueries, {
        sort_direction: sortOption.split('-')[1],
        sort_by: 'price'
      })
    } else {
      slugifiedQueries = Object.assign({}, slugifiedQueries, {
        sort_by: sortOption
      })
    }
    delete slugifiedQueries.sort

    /**
     * We should only keep valid sort by options so the server does not
     * throw error
     */
    if (validSortByOptions.indexOf(slugifiedQueries.sort_by) === -1) {
      delete slugifiedQueries.sort_by
    }
  }

  return slugifiedQueries
}

/**
 * Sanitize queries object by removing params with no value (empty string).
 * @param {Object} queries object
 * @returns {Object} new queries object
 */

export const sanitizeQueries = queries => {
  return Object.keys(queries).reduce((prev, curr) => {
    if (queries[curr] !== '') {
      const sanitizedValue = sanitizeQueryParamString(new String(queries[curr]))
      if (sanitizedValue === '') {
        delete queries[curr]
      } else {
        prev[curr] = encodeURIComponent(sanitizedValue)
          .replace(/%2C/g, ',')
          .replace(/%2F/g, '/')
          .replace(/%3A/g, ':')
      }
    }
    return prev
  }, {})
}

/**
 * Make a copy of the location object which pick the last option if it's an array
 * @param query
 * @returns {*}
 */
export const getFlattenQuery = query => {
  return Object.keys(query).reduce((prev, curr) => {
    if (Array.isArray(query[curr])) {
      const arrayLength = query[curr].length
      prev[curr] = query[curr][arrayLength - 1]
    } else {
      prev[curr] = query[curr]
    }
    return prev
  }, {})
}

export const getFlattenLocation = location => {
  const flattenQuery = getFlattenQuery(location.query)
  const flattenLocation = {
    ...location,
    query: flattenQuery
  }
  return flattenLocation
}

/**
 * Return the most SEO friendly URL in normal URL format.
 * prioritise order category > tags > attributes > search queries.
 * @deprecated Use makeSearchLink props which supports routes:new feature.
 * @param  {Object} queries object
 * @return {String} a SEO friendly URL
 */
export const makeLink = (queries = {}, currentSite = 'defaults') => {
  let url = ''
  let newQueries = sanitizeQueries(queries)
  /*
    We should not allow to show search results for pages greater than 60 because of the performance.
    So the purpose of this is to set a limit to being able to browser by the user maximum 60 pages.
   */
  if (
    newQueries.page &&
    Number(newQueries.page) > sitesConfig.defaults.common.max_pages_count
  ) {
    newQueries.page = sitesConfig.defaults.common.max_pages_count
  }

  /**
   * re-direct colours to color
   */
  if (newQueries.colours) {
    newQueries.colors = newQueries.colours
    delete newQueries.colours
  }

  // revert the process of decode sort_by value
  // when there's sort_by or sort_direction
  // we convert them to sort='price-desc'
  if (newQueries.sort_by) {
    if (newQueries.sort_by === 'price' && newQueries.sort_direction) {
      newQueries = Object.assign({}, newQueries, {
        sort: 'price-' + newQueries.sort_direction
      })
      delete newQueries.sort_direction
    } else {
      newQueries = Object.assign({}, newQueries, {
        sort: newQueries.sort_by
      })
    }
    delete newQueries.sort_by
  }

  if (Number(newQueries.page) === 1) {
    delete newQueries.page
  }
  if (newQueries.category) {
    // create a new copy of the current queries object
    let categories = newQueries.category
    delete newQueries.category
    const queriesString = getQueryString(newQueries)
    url = `/category/${categories}${queriesString}`
    // if the queries has tags, go to tags page
  } else if (newQueries.tags) {
    // create a new copy of the current queries object
    let tags = newQueries.tags.split(',').sort().join(',')
    delete newQueries.tags
    const queriesString = getQueryString(newQueries)
    url = `/tags/${tags}${queriesString}`
    // if the queries has attributes, go to attributes
  } else if (newQueries.attribute_key && newQueries.attribute_value) {
    // create a new copy of the current queries object
    let attribute_key = newQueries.attribute_key // eslint-disable-line
    let attribute_value = newQueries.attribute_value // eslint-disable-line
    delete newQueries.attribute_key
    delete newQueries.attribute_value
    const queriesString = getQueryString(newQueries)
    url = `/attributes/${attribute_key}/${attribute_value}${queriesString}` // eslint-disable-line
    // if the query has a term go to the search term page
  } else if (newQueries.term) {
    // clean up the search term in the url
    let term = newQueries.term.toLowerCase()
    delete newQueries.term
    const queriesString = getQueryString(newQueries)
    url = `/search/${term}${queriesString}`
    // else just to go search result page
  } else {
    const queriesString = getQueryString(newQueries)

    url = hasSearchPageQueryParams(newQueries)
      ? `/search${queriesString}`
      : `/category/all${queriesString}`
  }
  return url
}

const getQueryString = queries => {
  const string = querystring.stringify(queries, { encode: false })
  return string ? `?${string}` : ''
}

/**
 * get option array from query string
 * @param  {String} option string a,b,c
 * @return {Array} [a, b, c]
 */
export const getOptionsArrayFromQueryString = (optionsString = '') => {
  if (typeof optionsString !== 'string')
    throw new Error('tags should be a string.')
  return optionsString ? optionsString.split(',') : []
}

/**
 * get option string from query array
 * @param  {Array} option array [a, b, c]
 * @return {String} a,b,c
 */
export const getQueryStringFromOptionsArray = (optionsArray = []) => {
  if (!Array.isArray(optionsArray))
    throw new Error('tags array should be an array.')
  return optionsArray.join(',')
}

/**
 * Returns query without specified param
 * @param {Object} query
 * @param {String|Array} paramNames
 * @returns {Object}
 */
export const getQueryWithoutParam = (query, paramNames) => {
  return omit(query, paramNames)
}

/**
 * Returns query without specified option within the query param.
 * Eg. getQueryWithoutOption({t1: 'ab,cd,ef'}, 't1', 'cd') -> {t1: 'ab,ef'}
 * @param {Object} query
 * @param {String} paramName
 * @param {String} optionName
 * @returns {Object}
 */
export const getQueryWithoutOption = (query, paramName, optionName) => {
  let nextQuery = { ...query }
  let options = getOptionsArrayFromQueryString(query[paramName])
  options = options.filter(option => option !== optionName)
  if (options.length === 0) {
    delete nextQuery[paramName]
  } else {
    nextQuery[paramName] = getQueryStringFromOptionsArray(options)
  }
  return nextQuery
}

/**
 * Remove specific category and all its subcategories form a category query param
 * @param {Object} query
 * @param {String} paramName
 * @param {String} categoryName
 * @returns {Object}
 */
export const getQueryWithoutCategory = (query, paramName, categoryName) => {
  let nextQuery = { ...query }
  let categories = nextQuery[paramName].split('/')
  categories = categories.slice(0, categories.indexOf(categoryName))

  if (categories.length === 0) {
    delete nextQuery[paramName]
  } else {
    nextQuery[paramName] = categories.join('/')
  }

  return nextQuery
}
